import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import "./forms.css";

const API_BASE = "https://dairy.server.statify.co.ke";

function ProductFormContent(props) {
  const { noLayout } = props || {};
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditRoute = Boolean(id);

  const [form, setForm] = useState({ name: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // product list state
  const [products, setProducts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // modal/editor state (used for both create & edit)
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create"); // 'create' | 'edit'
  const [editorProduct, setEditorProduct] = useState({ id: "", name: "" });
  const [processingEditor, setProcessingEditor] = useState(false);

  // view modal state
  const [selectedProduct, setSelectedProduct] = useState(null);

  // load single product when editing via route (optional)
  useEffect(() => {
    if (!isEditRoute) return;
    setLoading(true);
    fetch(`${API_BASE}/api/products/${id}/`)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load product (${res.status})`);
        return res.json();
      })
      .then((data) => {
        setForm({ name: data.name || "" });
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to load product: " + (err.message || err));
      })
      .finally(() => setLoading(false));
  }, [id, isEditRoute]);

  // load product list
  useEffect(() => {
    loadProducts();
  }, []);

  function loadProducts() {
    setLoadingList(true);
    fetch(`${API_BASE}/api/products/`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Failed to fetch products"))))
      .then((list) => setProducts(Array.isArray(list) ? list : []))
      .catch((err) => {
        console.error("Load products error", err);
        setProducts([]);
      })
      .finally(() => setLoadingList(false));
  }

  // Save product (edit existing via route form)
  async function handleSave(e) {
    e.preventDefault();
    if (!form.name || !form.name.trim()) return alert("Product name is required");

    setSaving(true);
    try {
      const payload = { name: form.name.trim() };

      const url = isEditRoute ? `${API_BASE}/api/products/${id}/` : `${API_BASE}/api/products/`;
      const method = isEditRoute ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        try {
          const json = JSON.parse(text || "{}");
          throw new Error(json.error || json.message || text || res.statusText);
        } catch {
          throw new Error(text || res.statusText);
        }
      }

      alert("Product saved successfully!");
      loadProducts();
      navigate("/sales");
    } catch (err) {
      console.error(err);
      alert("Save failed: " + (err.message || err));
    } finally {
      setSaving(false);
    }
  }

  // open editor modal for create
  function openCreateModal() {
    setEditorMode("create");
    setEditorProduct({ id: "", name: "" });
    setEditorOpen(true);
  }

  // open editor modal for edit
  function openEditModal(prod) {
    setEditorMode("edit");
    setEditorProduct({ id: prod.id || "", name: prod.name || "" });
    setEditorOpen(true);
  }

  // create or update via editor modal
  async function handleEditorSave(e) {
    e && e.preventDefault();
    if (!editorProduct.name || !editorProduct.name.trim()) return alert("Product name is required");

    setProcessingEditor(true);
    try {
      const payload = { name: editorProduct.name.trim() };

      if (editorMode === "create") {
        // DO NOT send id during create; server should auto-generate
        const res = await fetch(`${API_BASE}/api/products/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          try {
            const json = JSON.parse(text || "{}");
            throw new Error(json.error || json.message || text || res.statusText);
          } catch {
            throw new Error(text || res.statusText);
          }
        }

        alert("Product created");
      } else {
        // edit mode: ID must exist but is NOT editable or shown in the modal
        const targetId = editorProduct.id;
        if (!targetId) return alert("Product ID is required to update");
        const res = await fetch(`${API_BASE}/api/products/${targetId}/`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          try {
            const json = JSON.parse(text || "{}");
            throw new Error(json.error || json.message || text || res.statusText);
          } catch {
            throw new Error(text || res.statusText);
          }
        }

        alert("Product updated");
      }

      setEditorOpen(false);
      loadProducts();
    } catch (err) {
      console.error(err);
      alert((editorMode === "create" ? "Create" : "Update") + " failed: " + (err.message || err));
    } finally {
      setProcessingEditor(false);
    }
  }

  async function handleDelete(prodId) {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API_BASE}/api/products/${prodId}/`, { method: "DELETE" });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        try {
          const j = JSON.parse(txt || "{}");
          throw new Error(j.error || j.message || txt || res.statusText);
        } catch {
          throw new Error(txt || res.statusText);
        }
      }
      loadProducts();
      alert("Product deleted");
    } catch (err) {
      console.error(err);
      alert("Delete failed: " + (err.message || err));
    }
  }

  function handleView(prod) {
    setSelectedProduct(prod);
  }

  function closeView() {
    setSelectedProduct(null);
  }

  return (
    <div className="form-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ margin: 0 }}>{isEditRoute ? "Edit Product" : "Products"}</h2>
        </div>

        {/* Create button on top that opens modal */}
        <div>
          <button className="btn" onClick={openCreateModal}>Create Product</button>
        </div>
      </div>

      {/* Optional route-based edit form (kept minimal) */}
      {isEditRoute && (
        <div style={{ marginTop: 16 }}>
          {loading ? (
            <div className="muted">Loading…</div>
          ) : (
            <form onSubmit={handleSave} className="form-grid" style={{ marginBottom: 24 }}>
              <div className="field">
                <label>Product name <span className="required">*</span></label>
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Raw Milk"
                  required
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-ghost small-btn" onClick={() => navigate(-1)}>
                  Cancel
                </button>
                <button type="submit" className="btn" disabled={saving}>
                  {saving ? "Saving..." : "Update Product"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Products list (ID & Name only) */}
      <div style={{ marginTop: 12 }}>

        {loadingList ? (
          <div className="muted">Loading products…</div>
        ) : products.length === 0 ? (
          <div className="muted">No products yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="compact-table" style={{ width: "100%", marginTop: 8 }}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th style={{ width: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>{p.name}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button type="button" className="btn-ghost small-btn" onClick={() => handleView(p)}>
                          View
                        </button>
                        <button type="button" className="btn small-btn" onClick={() => openEditModal(p)}>
                          Edit
                        </button>
                        <button type="button" className="btn btn-danger small-btn" onClick={() => handleDelete(p.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* editor modal used for both create & edit (shows Name only) */}
      {editorOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
          }}
          onClick={() => setEditorOpen(false)}
        >
          <div className="form-card" style={{ width: 480, maxWidth: "calc(100% - 32px)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}>{editorMode === "create" ? "Create Product" : "Edit Product"}</h3>
              <button className="btn-ghost small-btn" onClick={() => setEditorOpen(false)}>
                Close
              </button>
            </div>

            <form onSubmit={handleEditorSave} style={{ marginTop: 12 }}>
              <div className="field">
                <label>Product name <span className="required">*</span></label>
                <input
                  className="input"
                  value={editorProduct.name}
                  onChange={(e) => setEditorProduct({ ...editorProduct, name: e.target.value })}
                  placeholder="e.g. Raw Milk"
                  required
                />
              </div>

              <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                <button type="submit" className="btn" disabled={processingEditor}>
                  {processingEditor ? (editorMode === "create" ? "Creating..." : "Updating...") : (editorMode === "create" ? "Create" : "Update")}
                </button>
                <button type="button" className="btn-ghost" onClick={() => setEditorOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* simple view modal (ID & Name only) */}
      {selectedProduct && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
          }}
          onClick={() => setSelectedProduct(null)}
        >
          <div className="form-card" style={{ width: 420, maxWidth: "calc(100% - 32px)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}>{selectedProduct.name}</h3>
              <button className="btn-ghost small-btn" onClick={() => setSelectedProduct(null)}>
                Close
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              <div style={{ display: "grid", gap: 8 }}>
                <div>
                  <strong>ID</strong>
                  <div className="muted">{selectedProduct.id}</div>
                </div>

                <div>
                  <strong>Name</strong>
                  <div className="muted">{selectedProduct.name}</div>
                </div>
              </div>

              <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                <button
                  className="btn"
                  onClick={() => {
                    setSelectedProduct(null);
                    openEditModal(selectedProduct);
                  }}
                >
                  Edit
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    setSelectedProduct(null);
                    handleDelete(selectedProduct.id);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Default export: wrap with DashboardLayout unless caller passes { noLayout: true } prop.
 */
export default function ProductForm(props) {
  const { noLayout } = props || {};
  const content = <ProductFormContent {...props} />;

  if (noLayout) return content;

  // Wrap with DashboardLayout so when navigating to /product-form it renders inside the app shell.
  return <DashboardLayout>{content}</DashboardLayout>;
}
